type ImageCompressResult = Blob | null;

const readFileAsDataURL = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

const canvasToBlob = (
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> => {
  return new Promise((resolve) => {
    canvas.toBlob(resolve, type, quality);
  });
};

export const compressImage = async (
  file: File,
  options: {
    quality?: number; // Between 0 to 1
    maxWidth?: number;
    maxHeight?: number;
  } = {},
): Promise<ImageCompressResult> => {
  const { quality = 0.5, maxWidth = 1200, maxHeight = 1600 } = options;

  if (!file.type.startsWith("image/")) {
    return null;
  }

  try {
    const dataUrl = await readFileAsDataURL(file);

    const img = await loadImage(dataUrl);

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      return null;
    }

    const { width: newWidth, height: newHeight } = calculateOptimalDimensions(
      img.width,
      img.height,
      maxWidth,
      maxHeight,
    );

    canvas.width = newWidth;
    canvas.height = newHeight;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    const adjustedQuality = Math.max(0.8, quality);

    return await canvasToBlob(canvas, file.type, adjustedQuality);
  } catch (error) {
    console.error("Image compression failed:", error);
    return null;
  }
};

// Checks the largest scale factor that we can use to downscale the image
function calculateOptimalDimensions(
  originalWidth: number,
  originalHeight: number,
  maxWidth: number,
  maxHeight: number,
): { width: number; height: number } {
  let newWidth = originalWidth;
  let newHeight = originalHeight;

  const widthRatio = maxWidth / originalWidth;
  const heightRatio = maxHeight / originalHeight;

  // Only downscaling. If the scaleFactor is higher than 1, don't do anything
  const scaleFactor = Math.min(widthRatio, heightRatio, 1);

  newWidth = Math.round(originalWidth * scaleFactor);
  newHeight = Math.round(originalHeight * scaleFactor);

  return { width: newWidth, height: newHeight };
}
